/**
 * The notification chime.
 *
 * ── Why it is synthesised rather than a file ────────────────────────────────
 *
 * A two-note chime is about thirty lines of Web Audio and about 15KB as an
 * audio file — in two formats, because one of them will be the one a given
 * browser refuses. Synthesised there is nothing to ship, nothing to 404, and
 * nothing to get out of step with the build; the tone is a number in this file
 * rather than a binary somebody has to open an editor to change.
 *
 * ── Why the sound is short, quiet and unresolved ────────────────────────────
 *
 * This plays in an office. It has to be audible over a keyboard and forgettable
 * ten seconds later, which means: under a fifth of a second, well below speech
 * volume, and no bass — bass is what carries through a wall and annoys the
 * person at the next desk, who did not get the message.
 *
 * The two notes rise a perfect fifth for a message and fall for work, so
 * somebody who hears one across the room knows which it was without looking.
 * Nothing resolves to a tonic: a sound that feels finished invites attention,
 * and this is a tap on the shoulder, not an announcement.
 */

/*
 * ── The autoplay rule ──────────────────────────────────────────────────────
 *
 * A browser will not let a page make a noise until the person has interacted
 * with it. An AudioContext created before that starts `suspended` and stays
 * silent, so the first notification of a session — the one that tells somebody
 * the feature works — would be the one nobody heard.
 *
 * So the context is created on the first real gesture, which is almost always
 * the click that signs them in, and `resume()` is attempted again on every play
 * in case the browser suspended it since (which it does after a tab has been in
 * the background for a while).
 */
let context = null
let unlocked = false

const GESTURES = ["pointerdown", "keydown", "touchstart"]

function ensureContext() {
  if (context) return context
  const Ctor = window.AudioContext || window.webkitAudioContext
  if (!Ctor) return null
  try {
    context = new Ctor()
  } catch {
    context = null
  }
  return context
}

/** Called once from a real gesture, so the first notification can be heard. */
function unlock() {
  unlocked = true
  const ctx = ensureContext()
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {})
  for (const event of GESTURES) window.removeEventListener(event, unlock)
}

export function armNotificationSound() {
  if (typeof window === "undefined" || unlocked) return
  for (const event of GESTURES) window.addEventListener(event, unlock, { once: true, passive: true })
}

/*
 * ── Not once per message ───────────────────────────────────────────────────
 *
 * Ten messages arriving in a busy minute is ten chimes overlapping into a
 * noise, and the honest description of that is an alarm. One sound every two
 * seconds says the same thing — something arrived — without the room turning
 * round.
 */
const MIN_GAP_MS = 2000
let lastPlayed = 0

/** The two notes, in Hz. Nothing below 500: bass is what travels through walls. */
const VOICES = {
  // A perfect fifth up. Open, and unmistakably "someone said something".
  message: [660, 990],
  // The same interval down: an arrival that asks for something rather than
  // offering it.
  task: [880, 587],
  // Three notes and a touch louder. Reserved for high priority, which is why
  // it is allowed to be the one that interrupts.
  urgent: [784, 1046, 1318],
}

function voiceFor({ refType, priority } = {}) {
  if (priority === "high") return VOICES.urgent
  if (refType === "chat_room") return VOICES.message
  return VOICES.task
}

/**
 * Play the chime.
 *
 * @param {object} [options]
 * @param {string} [options.refType]  What arrived, which picks the interval.
 * @param {string} [options.priority] `high` gets the three-note figure.
 * @param {boolean} [options.force]   Skip the throttle. For the sample button
 *   in settings, where the whole point is that pressing it makes a sound.
 * @returns {boolean} Whether anything was scheduled — false means the browser
 *   has no Web Audio at all, or the throttle swallowed this one. Scheduled is
 *   not the same as audible: a context the browser never resumes stays silent,
 *   and there is no way to ask it whether a sound was heard.
 */
export function playNotificationSound(options = {}) {
  if (typeof window === "undefined") return false

  const now = Date.now()
  if (!options.force && now - lastPlayed < MIN_GAP_MS) return false

  const ctx = ensureContext()
  if (!ctx) return false
  /*
   * Ask for a resume and schedule anyway.
   *
   * `resume()` is asynchronous, so checking the state straight afterwards and
   * bailing would drop the first chime of every session — the one that tells
   * somebody the feature works. It does not need to be awaited: a suspended
   * context's `currentTime` does not advance, so notes scheduled relative to it
   * are still in the future when it starts, and they play then. If the browser
   * never resumes it, nothing is heard and nothing is broken.
   */
  if (ctx.state === "suspended") ctx.resume().catch(() => {})

  const notes = voiceFor(options)
  const peak = options.priority === "high" ? 0.1 : 0.07
  const start = ctx.currentTime

  try {
    notes.forEach((frequency, index) => {
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()

      // A triangle wave, not a sine and not a square. A sine at this volume
      // disappears under a fan; a square is a buzzer.
      oscillator.type = "triangle"
      oscillator.frequency.value = frequency

      const at = start + index * 0.085
      /*
       * The envelope is the whole difference between a chime and a beep.
       *
       * A gain that jumps from 0 to full produces a click — the discontinuity
       * is a broadband transient, and it is what makes cheap notification
       * sounds sound cheap. 12ms in, then an exponential decay, which is how a
       * struck object actually behaves.
       *
       * Ramps go to 0.0001 rather than 0 because exponentialRampToValueAtTime
       * cannot reach zero.
       */
      gain.gain.setValueAtTime(0.0001, at)
      gain.gain.exponentialRampToValueAtTime(peak, at + 0.012)
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.17)

      oscillator.connect(gain)
      gain.connect(ctx.destination)
      oscillator.start(at)
      oscillator.stop(at + 0.19)
    })
  } catch {
    return false
  }

  lastPlayed = now
  return true
}

/** Whether this browser can make a sound at all, for the settings card. */
export function soundSupported() {
  return typeof window !== "undefined" && Boolean(window.AudioContext || window.webkitAudioContext)
}
